/**
 * As notas da oportunidade, dentro do diálogo (criar e editar).
 *
 * ── O mesmo bloco, dois estados de vida ───────────────────────────────────
 *
 * Na EDIÇÃO a oportunidade existe: cada nota é gravada assim que a pessoa
 * confirma, e a tabela vem do servidor. Na CRIAÇÃO ela ainda não existe, então
 * as notas ficam na tela e viajam junto no pedido de criar.
 *
 * A diferença não é enfeite: gravar na edição evita perder o que foi escrito
 * se o salvamento falhar por conflito de versão — a nota não depende do resto
 * do formulário. E na criação não há onde gravar antes de haver id.
 *
 * O que as duas compartilham é a TABELA e o CAMPO, que é o que a pessoa vê.
 *
 * ── Tabela de verdade, e não uma lista ────────────────────────────────────
 *
 * São três colunas com o mesmo significado em toda linha — quando, quem, o
 * quê. É tabela, e marcá-la como tal é o que faz o leitor de tela anunciar
 * "coluna Autor" ao navegar. Uma `ul` com texto corrido leria tudo emendado.
 *
 * ── Corrigir sim, apagar não ──────────────────────────────────────────────
 *
 * A correção existe para o erro de digitação: sem ela, arrumar uma palavra
 * obrigaria a escrever "onde se lê 44, leia-se 48", e a tabela viraria uma
 * conversa com ela mesma. Ela aparece só no diálogo de EDITAR — na ficha a
 * pessoa está acompanhando a negociação, não arrumando dado.
 *
 * Apagar não existe em lugar nenhum: nota que some não deixa rastro de que
 * houve, e o histórico deixaria de valer como registro. Na CRIAÇÃO o "Tirar"
 * existe, e não é contradição — ali nada foi gravado ainda.
 *
 * ── E a linha corrigida diz que foi corrigida ─────────────────────────────
 *
 * `editada_em` vira a marca "editada" ao lado da data. Sem ela o texto
 * reescrito continuaria parecendo o que foi dito no dia — que é pior que não
 * ter deixado corrigir, porque parece registro sem ser.
 */

import { useState, type ReactNode } from 'react';
import type { NotaDaOportunidade } from '@cliqcrm/contracts';
import { useNotasDaOportunidade } from '@/shared/api/consultas';
import { Botao, CampoArea } from '@/shared/ui';

const LIMITE = 2000;

/** O `title` da marca de editada — data por extenso, que a marca não cabe. */
function tituloDaEdicao(instante: string): string {
  return 'Corrigida em ' + quando(instante);
}

/** Data e hora curtas: a nota é do dia a dia, e o ano quase nunca importa. */
function quando(instante: string): string {
  const data = new Date(instante);
  if (Number.isNaN(data.getTime())) return '—';
  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function TabelaDeNotas({
  itens,
  carregando = false,
  aoRemover,
  aoCorrigir,
  corrigindo = false,
  autorAusente = 'autor removido',
}: {
  itens: readonly NotaDaOportunidade[];
  carregando?: boolean;
  /** Só a criação passa: ali a nota ainda não foi gravada. Ver o cabeçalho. */
  aoRemover?: (indice: number) => void;
  /** Só o diálogo de editar passa. Ver o cabeçalho. */
  aoCorrigir?: (notaId: string, texto: string) => void;
  corrigindo?: boolean;
  /**
   * O que mostrar quando não há autor.
   *
   * Na edição, autor nulo quer dizer que quem escreveu saiu da revenda. Na
   * criação quer dizer "ainda não foi gravada, e quem está escrevendo é você" —
   * duas coisas diferentes que a mesma coluna vazia esconderia.
   */
  autorAusente?: string;
}): ReactNode {
  /* Qual linha está aberta para correção — uma por vez, e o texto em edição. */
  const [emCorrecao, definirEmCorrecao] = useState<{ id: string; texto: string } | null>(null);

  if (carregando) {
    return <p className="notas__vazio">Buscando o que já foi anotado…</p>;
  }

  if (itens.length === 0) {
    return (
      <p className="notas__vazio">
        {/*
          O vazio diz PARA QUE serve, e não "nenhum registro encontrado". Este
          é o campo que fica em branco quando ninguém entendeu o que ele é.
        */}
        Nada anotado ainda. Escreva o que foi combinado, como prazo, condição ou objeção, para quem
        abrir esta oportunidade depois não precisar adivinhar.
      </p>
    );
  }

  return (
    <table className="notas__tabela">
      <caption className="notas__legenda">
        {itens.length === 1 ? '1 nota' : `${itens.length} notas`}, da mais recente para a mais
        antiga
      </caption>
      <thead>
        <tr>
          <th scope="col">Quando</th>
          <th scope="col">Quem</th>
          <th scope="col">Nota</th>
          {(aoRemover || aoCorrigir) && (
            <th scope="col">
              <span className="notas__coluna-oculta">Ações</span>
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {itens.map((nota, indice) => (
          <tr key={nota.id}>
            <td className="notas__quando">
              {quando(nota.criada_em)}
              {nota.editada_em && (
                // A marca fica na linha, e não num rodapé: quem lê a nota
                // precisa saber AO LER que aquele texto foi reescrito depois.
                <span className="notas__editada" title={tituloDaEdicao(nota.editada_em)}>
                  editada
                </span>
              )}
            </td>
            <td className="notas__quem">
              {/*
                A nota sobrevive à saída de quem a escreveu, e a tela diz isso
                em vez de mostrar um traço: quem lê precisa saber que houve um
                autor, e que ele não está mais aqui.
              */}
              {nota.autor?.nome ?? (nota.autor ? 'sem nome' : autorAusente)}
            </td>
            <td className="notas__texto">
              {emCorrecao?.id === nota.id ? (
                <div className="notas__corrigindo">
                  <CampoArea
                    rotulo="Corrigir a nota"
                    rows={2}
                    value={emCorrecao.texto}
                    maxLength={LIMITE}
                    autoFocus
                    onChange={(evento) =>
                      definirEmCorrecao({ id: nota.id, texto: evento.target.value })
                    }
                  />
                  <div className="notas__corrigindo-acoes">
                    <Botao
                      miudo
                      onClick={() => {
                        const texto = emCorrecao.texto.trim();
                        // Texto igual ao que já está não é correção: mandar
                        // marcaria a nota como editada sem nada ter mudado.
                        if (texto !== '' && texto !== nota.texto) aoCorrigir?.(nota.id, texto);
                        definirEmCorrecao(null);
                      }}
                      desabilitado={corrigindo || emCorrecao.texto.trim() === ''}
                      motivo={corrigindo ? 'Salvando a correção…' : 'A nota não pode ficar vazia.'}
                    >
                      Salvar
                    </Botao>
                    <Botao miudo onClick={() => definirEmCorrecao(null)}>
                      Cancelar
                    </Botao>
                  </div>
                </div>
              ) : (
                nota.texto
              )}
            </td>
            {(aoRemover || aoCorrigir) && (
              <td>
                {aoRemover && (
                  <Botao
                    miudo
                    title="Tira esta nota antes de criar a oportunidade."
                    onClick={() => aoRemover(indice)}
                  >
                    Tirar
                  </Botao>
                )}
                {aoCorrigir && emCorrecao?.id !== nota.id && (
                  <Botao
                    miudo
                    title="Corrige o texto desta nota. A linha passa a mostrar que foi editada."
                    onClick={() => definirEmCorrecao({ id: nota.id, texto: nota.texto })}
                  >
                    Corrigir
                  </Botao>
                )}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * O campo de escrever, com o botão ao lado.
 *
 * `Enter` não envia: a nota é texto de duas ou três linhas, e um campo que
 * some ao primeiro `Enter` faz perder metade do que estava sendo escrito.
 */
export function CampoDeNota({
  aoEscrever,
  salvando = false,
  erro,
}: {
  aoEscrever: (texto: string) => void;
  salvando?: boolean;
  erro?: string | null;
}): ReactNode {
  const [texto, definirTexto] = useState('');
  const limpo = texto.trim();

  return (
    <div className="notas__escrever">
      {erro != null && (
        <p className="dialogo__erro" role="alert">
          {erro}
        </p>
      )}

      <CampoArea
        rotulo="Escrever uma nota"
        rows={2}
        value={texto}
        maxLength={LIMITE}
        placeholder="Ex.: prefere pagar à vista, mas só depois do dia 10"
        ajuda="A nota entra e fica: para corrigir, escreva outra."
        onChange={(evento) => definirTexto(evento.target.value)}
      />

      <Botao
        onClick={() => {
          if (limpo === '' || salvando) return;
          aoEscrever(limpo);
          definirTexto('');
        }}
        desabilitado={limpo === '' || salvando}
        motivo={salvando ? 'Gravando a nota…' : 'Escreva algo para anotar.'}
      >
        {salvando ? 'Anotando…' : 'Anotar'}
      </Botao>
    </div>
  );
}

/**
 * O bloco da EDIÇÃO: lê do servidor e grava cada nota na hora.
 *
 * Recebe `aoEscrever` de fora em vez de chamar a API aqui porque quem conhece
 * a chave de idempotência e a invalidação do cache é a tela do funil — e
 * espalhar isso por dois lugares faria a lista atualizar num e não no outro.
 */
export function NotasDaOportunidade({
  oportunidadeId,
  aoEscrever,
  salvando,
  erro,
}: {
  oportunidadeId: string;
  aoEscrever: (texto: string) => void;
  salvando: boolean;
  erro?: string | null;
}): ReactNode {
  const consulta = useNotasDaOportunidade(oportunidadeId);

  return (
    <section className="notas">
      <h3 className="notas__titulo">Notas</h3>
      <TabelaDeNotas itens={consulta.data?.itens ?? []} carregando={consulta.isPending} />
      <CampoDeNota aoEscrever={aoEscrever} salvando={salvando} erro={erro} />
    </section>
  );
}

/**
 * O bloco da CRIAÇÃO: as notas ficam aqui até a oportunidade existir.
 *
 * Elas vão no mesmo pedido de criar — não há o desfecho em que o negócio nasce
 * e o que foi combinado se perde numa segunda chamada que falhou.
 */
export function NotasPendentes({
  notas,
  aoMudar,
}: {
  notas: readonly string[];
  aoMudar: (notas: readonly string[]) => void;
}): ReactNode {
  /*
   * O id local existe só para a tabela ter chave estável.
   *
   * Usar o índice faria o React reaproveitar a linha errada ao tirar uma nota
   * do meio — e o texto de uma apareceria na linha de outra por um instante.
   */
  const itens: NotaDaOportunidade[] = notas.map((texto, indice) => ({
    id: `pendente-${indice}-${texto.slice(0, 12)}`,
    texto,
    autor: null,
    criada_em: new Date().toISOString(),
    // Nada foi gravado ainda: não há o que ter sido corrigido.
    editada_em: null,
  }));

  return (
    <section className="notas">
      <h3 className="notas__titulo">Notas</h3>
      <TabelaDeNotas
        itens={itens}
        autorAusente="você"
        aoRemover={(indice) => aoMudar(notas.filter((_, i) => i !== indice))}
      />
      <CampoDeNota aoEscrever={(texto) => aoMudar([...notas, texto])} />
    </section>
  );
}

/**
 * O bloco do diálogo de EDITAR: corrige o texto, e não escreve nota nova.
 *
 * Sem o campo de escrever, de propósito. Escrever é acompanhar a negociação, e
 * isso acontece na ficha; aqui a pessoa veio arrumar dado. Ter os dois em dois
 * lugares faria a mesma tabela se comportar diferente conforme de onde se
 * chegou nela.
 */
export function NotasParaCorrigir({
  oportunidadeId,
  aoCorrigir,
  corrigindo,
  erro,
}: {
  oportunidadeId: string;
  aoCorrigir: (notaId: string, texto: string) => void;
  corrigindo: boolean;
  erro?: string | null;
}): ReactNode {
  const consulta = useNotasDaOportunidade(oportunidadeId);

  return (
    <section className="notas">
      <h3 className="notas__titulo">Notas</h3>
      <p className="notas__apoio">
        Corrigir troca o texto e marca a linha como editada. Para contar algo novo, escreva na ficha
        da oportunidade — assim o histórico continua na ordem em que aconteceu.
      </p>
      {erro != null && (
        <p className="dialogo__erro" role="alert">
          {erro}
        </p>
      )}
      <TabelaDeNotas
        itens={consulta.data?.itens ?? []}
        carregando={consulta.isPending}
        aoCorrigir={aoCorrigir}
        corrigindo={corrigindo}
      />
    </section>
  );
}
